import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getCheckMailingAddress } from "@/hera-api/client";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useQuery } from "@tanstack/react-query";

interface CheckPaymentSuccessDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  displayId: string;
  checkMailingAddress?: string | null;
  /** Optional amount to pay (e.g. for bulk check payment). Shown as "Amount to pay: {amountToPay}" */
  amountToPay?: string | null;
  /** ``invoice`` shows invoice-specific copy (no mailing address). */
  variant?: "check" | "invoice";
  onOk?: () => void;
}

export function CheckPaymentSuccessDialog({
  isOpen,
  onOpenChange,
  displayId,
  checkMailingAddress: checkMailingAddressProp,
  amountToPay,
  variant = "check",
  onOk,
}: CheckPaymentSuccessDialogProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const isInvoice = variant === "invoice";
  const { data: addressData } = useQuery({
    queryKey: ["check-mailing-address"],
    queryFn: async () => {
      const response = await getCheckMailingAddress();
      if (response.error) throw response.error;
      return response.data;
    },
    enabled:
      !isInvoice &&
      isAuthSetupComplete &&
      isOpen &&
      !!displayId &&
      (checkMailingAddressProp == null ||
        String(checkMailingAddressProp).trim() === ""),
  });

  const checkMailingAddress =
    checkMailingAddressProp ?? addressData?.check_mailing_address ?? null;

  const handleOk = () => {
    onOk?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>
            {isInvoice ? "Invoice Created" : "Check Payment Created"}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-foreground pt-2 space-y-3">
              {amountToPay && (
                <p>
                  <span className="font-medium">Amount to pay: </span>
                  <strong>{amountToPay}</strong>
                </p>
              )}
              {isInvoice ? (
                <p>
                  Your invoice has been recorded. Reference ID:{" "}
                  <strong>{displayId}</strong>. There is no card or check
                  processing fee on invoice lines. Accounts contacts on your
                  network will receive a confirmation email with payment
                  instructions.
                </p>
              ) : (
                <p>
                  Your check payment has been recorded. Please include this
                  Payment ID with your check: <strong>{displayId}</strong>
                </p>
              )}
              {!isInvoice && checkMailingAddress && (
                <div>
                  <p className="font-medium mb-1">Send your check to:</p>
                  <pre className="whitespace-pre-wrap font-sans text-sm bg-muted/50 p-3 rounded-md">
                    {`HERA Registry\n${checkMailingAddress}`}
                  </pre>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button onClick={handleOk}>OK</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
